// vg-binding: P7.D-134
// vg-binding: P7.D-154
//
// apps/vendor/src/pages/application/ReviewPage.tsx
//
// Phase 1 Wave 12 Task 99 — Vendor application step 5 (review + submit).
// Phase 7 Wave 18 Task 78 batch C6 (Amendment #1) — i18n rewrite.
// P0-2 (vendor-audit 2026-08-02): submit rewired to the REAL endpoint
// POST /api/v1/vendor/onboarding/submit. The Wave-12 stub posted to a
// nonexistent /vendor/application/submit, swallowed the 404 and showed a
// success toast — every application was silently lost. Now fail-closed on
// EVERY non-2xx.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  FormMessage,
  VendorApplicationStepperLayout,
  useToast,
} from '@printway/ui';

import { csrfFetch } from '../../lib/api/csrfFetch.js';
import { i18n, useTranslation } from '../../lib/i18n/index.js';
import { useApplicationStore } from './ApplicationStore.js';

interface SectionProps {
  title: string;
  editTo: string;
  children: React.ReactNode;
}

function Section({ title, editTo, children }: SectionProps): JSX.Element {
  const { t } = useTranslation('onboarding');
  return (
    <div className="rounded-lg border border-pw-border bg-pw-bg p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-pw-text">{title}</h3>
        <Link
          to={editTo}
          className="text-xs font-medium text-pw-primary-strong hover:underline"
        >
          {t('application.review.edit')}
        </Link>
      </div>
      <dl className="mt-3 space-y-1.5 text-sm">{children}</dl>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="text-pw-text-muted">{label}</dt>
      <dd className="text-right font-medium text-pw-text">{value || '—'}</dd>
    </div>
  );
}

export function ReviewPage(): JSX.Element {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { toast } = useToast();
  const data = useApplicationStore((s) => s.data);
  const setTermsAccepted = useApplicationStore((s) => s.setTermsAccepted);
  const getOrCreateSubmitKey = useApplicationStore((s) => s.getOrCreateSubmitKey);
  const reset = useApplicationStore((s) => s.reset);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { businessInfo, storeSetup, documents, bankDetails, termsAccepted } = data;
  const canSubmit = termsAccepted && !submitting;

  const handleSubmit = async (): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Idempotency-Key persisted in store — same key reused across retries
      // so BE can dedupe a double-submit (CrossAI fix).
      const idemKey = getOrCreateSubmitKey();

      // Map wizard state → OnboardingSubmitSchema (apps/api vendor/schemas/
      // onboarding.ts). Notes:
      //  - country is the ISO-2 select value from step 1;
      //  - timezone is not collected by the wizard — browser timezone;
      //  - the wizard has no warehouse step, so the business address becomes
      //    the first dispatch origin (BE requires >= 1 warehouse);
      //  - routingNumber has no BE field (strict schema) — review-only;
      //  - kyc_doc_ids carries only really-uploaded docs (insurance slot is
      //    metadata-only, no matching KYC doc_type).
      const language = ['en', 'vi', 'fr'].includes(i18n.language)
        ? (i18n.language as 'en' | 'vi' | 'fr')
        : 'en';
      const kycDocIds = [
        documents.businessLicense?.docId,
        documents.taxId?.docId,
        documents.insuranceCertificate?.docId,
      ].filter((v): v is string => typeof v === 'string' && v.length > 0);
      const submitBody = {
        business_info: {
          business_name: storeSetup.storeName || businessInfo.fullName,
          legal_name: businessInfo.fullName,
          contact_email: businessInfo.email,
          contact_phone: businessInfo.phone,
          country: businessInfo.country,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language,
        },
        kyc_doc_ids: kycDocIds,
        bank_account: {
          method: 'bank' as const,
          holder_name: bankDetails.accountHolderName,
          account_number: bankDetails.accountNumber,
          ...(bankDetails.bankName ? { bank_name: bankDetails.bankName } : {}),
          ...(businessInfo.country
            ? { bank_country: businessInfo.country }
            : {}),
        },
        warehouses: [
          {
            name: storeSetup.storeName || businessInfo.fullName,
            address: [
              businessInfo.street,
              businessInfo.city,
              businessInfo.state,
              businessInfo.postalCode,
            ]
              .filter(Boolean)
              .join(', '),
            country: businessInfo.country,
            priority: 50,
            is_active: true,
          },
        ],
        review_confirmed: true as const,
      };

      const res = await csrfFetch('/api/v1/vendor/onboarding/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idemKey,
        },
        body: JSON.stringify(submitBody),
      });
      const json = (await res.json().catch(() => null)) as {
        ok?: boolean;
        error?: { message?: string; user_message?: string };
      } | null;
      if (!res.ok || !json?.ok) {
        throw new Error(
          json?.error?.user_message ??
            json?.error?.message ??
            t('application.review.submitFailedHttp', { status: res.status }),
        );
      }
      toast({
        title: t('application.review.toastTitle'),
        description: t('application.review.toastDescription'),
      });
      navigate('/application/pending-review');
      // Reset after navigation so PendingReviewPage can read final state if needed.
      setTimeout(() => reset(), 1000);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : t('application.review.submitFailedGeneric');
      setSubmitError(msg);
      setSubmitting(false);
    }
  };

  return (
    <VendorApplicationStepperLayout currentStep={5}>
      <h2 className="text-xl font-semibold text-pw-text">{t('application.review.heading')}</h2>
      <p className="mt-1 text-sm text-pw-text-muted">
        {t('application.review.description')}
      </p>

      <div className="mt-6 space-y-4">
        <Section title={t('application.review.businessSection')} editTo="/application/business-info">
          <Row label={t('application.review.rowType')} value={businessInfo.businessType} />
          <Row label={t('application.review.rowName')} value={businessInfo.fullName} />
          <Row label={t('application.review.rowEmail')} value={businessInfo.email} />
          <Row label={t('application.review.rowPhone')} value={businessInfo.phone} />
          <Row
            label={t('application.review.rowAddress')}
            value={[
              businessInfo.street,
              businessInfo.city,
              businessInfo.state,
              businessInfo.postalCode,
              businessInfo.country,
            ]
              .filter(Boolean)
              .join(', ')}
          />
        </Section>

        <Section title={t('application.review.storeSection')} editTo="/application/store-setup">
          <Row label={t('application.review.rowStoreName')} value={storeSetup.storeName} />
          <Row label={t('application.review.rowStoreType')} value={storeSetup.storeType} />
          <Row label={t('application.review.rowCapacity')} value={storeSetup.capacityPerDay} />
          <Row label={t('application.review.rowEquipment')} value={storeSetup.equipment.join(', ')} />
        </Section>

        <Section title={t('application.review.docsSection')} editTo="/application/documents">
          <Row label={t('application.review.rowBusinessLicense')} value={documents.businessLicense?.name ?? '—'} />
          <Row label={t('application.review.rowTaxId')} value={documents.taxId?.name ?? '—'} />
          <Row label={t('application.review.rowInsurance')} value={documents.insuranceCertificate?.name ?? '—'} />
        </Section>

        <Section title={t('application.review.bankSection')} editTo="/application/bank-details">
          <Row label={t('application.review.rowAccountHolder')} value={bankDetails.accountHolderName} />
          <Row label={t('application.review.rowBank')} value={bankDetails.bankName} />
          <Row
            label={t('application.review.rowAccountNumber')}
            value={
              bankDetails.accountNumber
                ? `••••${bankDetails.accountNumber.slice(-4)}`
                : '—'
            }
          />
        </Section>
      </div>

      <label className="mt-6 flex items-start gap-3 rounded-lg border border-pw-border bg-pw-surface p-4">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-pw-primary"
        />
        <span className="text-sm text-pw-text">
          {t('application.review.termsPrefix')}{' '}
          <a href="/terms" className="text-pw-primary-strong underline" target="_blank" rel="noreferrer">
            {t('application.review.termsLink')}
          </a>{' '}
          {t('application.review.termsMiddle')}{' '}
          <a href="/privacy" className="text-pw-primary-strong underline" target="_blank" rel="noreferrer">
            {t('application.review.privacyLink')}
          </a>
          {t('application.review.termsSuffix')}
        </span>
      </label>

      {submitError ? (
        <FormMessage className="mt-3">{submitError}</FormMessage>
      ) : null}

      <div className="mt-6 flex items-center justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/application/bank-details')}
          disabled={submitting}
        >
          {t('application.review.back')}
        </Button>
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {submitting ? t('application.review.submitting') : t('application.review.submit')}
        </Button>
      </div>
    </VendorApplicationStepperLayout>
  );
}

export default ReviewPage;
